import { startCase } from "lodash";
import { ReactNode, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { useCrudRoutes, useNavigatePathsAdmin } from "@ryde/redux";
import { ISettingsSubItem, RydeAdminUnit, RydeAppUnit } from "@ryde/shared";

export interface ICreateDefaultMenuItemOptions {
  /** When set, the row runs this handler instead of linking to the entity list. */
  buttonModeClickEventHandler?: () => void;
  /** i18n key for `t()`; default is `` `${entity}.plural` `` */
  titleTranslationKey?: string;
  /** When set, used as `href` instead of the default entity list URL (same `permission` still uses `entity`). */
  href?: string;
  leftIcon?: ReactNode;
}

interface ICreateMobileHandleMenuItem {
  title: string;
  active?: boolean;
  onClick?: () => void;
  customRightImage?: ReactNode;
}

export const useCreateSettingsItem = () => {
  const appNavigate = useCrudRoutes();
  const appNavigateAdmin = useNavigatePathsAdmin();
  const { t } = useTranslation();

  const createDefaultMenuItem = (
    entity: RydeAppUnit,
    options?: ICreateDefaultMenuItemOptions,
  ): ISettingsSubItem => {
    const {
      buttonModeClickEventHandler,
      href: hrefOverride,
      titleTranslationKey,
      leftIcon,
    } = options ?? {};
    const titleKey = titleTranslationKey ?? `${entity}.plural`;
    return {
      title: startCase(t(titleKey) || undefined),
      ...(leftIcon ? { leftIcon } : {}),
      ...(buttonModeClickEventHandler
        ? { buttonModeClickEventHandler }
        : { href: hrefOverride ?? appNavigate[entity]?.listUrl }),
      permission: {
        entity,
        action: "read",
      },
    };
  };

  const createDefaultMenuItemAdmin = useCallback(
    (
      entity: RydeAdminUnit,
      options?: ICreateDefaultMenuItemOptions,
    ): ISettingsSubItem => {
      const { titleTranslationKey, href: hrefOverride } = options ?? {};
      const titleKey = titleTranslationKey ?? `${entity}.plural`;
      const translatedTitle = t(titleKey) || undefined;

      return {
        title: titleTranslationKey
          ? (translatedTitle ?? "")
          : startCase(translatedTitle),
        href: hrefOverride ?? appNavigateAdmin[entity]?.listUrl,
        permission: {
          entity,
          action: "read",
        },
      };
    },
    [t, appNavigateAdmin],
  );

  /**
   * Menu item without permission check at sidebar level.
   * Permissions inside the page are checked for the selected category.
   */
  const createDefaultMenuItemAdminInnerPermissionCheck = useCallback(
    (entity: RydeAdminUnit): ISettingsSubItem => {
      const specialTitles: Partial<Record<RydeAdminUnit, string>> = {
        mapCacheStat: t("mapCacheStat.plural"),
        adminTools: t("adminTools.plural"),
      };
      const title =
        specialTitles[entity] ?? startCase(t(`${entity}.plural`) || undefined);
      return {
        title,
        href: appNavigateAdmin[entity]?.listUrl,
        skipPermissionCheck: true,
      };
    },
    [t, appNavigateAdmin],
  );

  const createMobileHandleMenuItem = (
    itemParams: ICreateMobileHandleMenuItem,
  ): ISettingsSubItem => {
    const { title, onClick, customRightImage, active } = itemParams;
    return {
      title: startCase(t(`${title}`) || ""),
      skipPermissionCheck: true,
      buttonModeClickEventHandler: onClick,
      customRightImage,
      active,
    };
  };

  return {
    createDefaultMenuItem,
    createDefaultMenuItemAdmin,
    createDefaultMenuItemAdminNoPermission:
      createDefaultMenuItemAdminInnerPermissionCheck,
    createMobileHandleMenuItem,
  };
};
